// aiJsonStrip — Skrubba rå workflow-JSON från AI-chat-meddelanden innan
// rendering och persist till DB. Täcker både ` ```json ... ``` `-fenced och
// ofenced top-level JSON-objekt — den senare gruppen är en exploit-vektor
// eftersom AI ibland glömmer fence-wrap trots system-prompten.
//
// Heuristik: ett `{...}`-block räknas som workflow-JSON om det innehåller
// minst TVÅ signatur-nycklar (blocks/connectors/groups/sheets eller deras
// compact-motsvarigheter b/cn/g). Det undviker falsk-positiva på små inline-
// objekt som `{"status": "ok"}` etc.

const WORKFLOW_KEY_PATTERNS: RegExp[] = [
  /"blocks"\s*:/,
  /"connectors"\s*:/,
  /"groups"\s*:/,
  /"sheets"\s*:/,
  /"crossSheetConnectors"\s*:/,
  // Compact-format signatures (set by compactWorkflow.ts).
  /"b"\s*:\s*\[/,
  /"cn"\s*:\s*\[/,
  /"g"\s*:\s*\[/,
];

const PLACEHOLDER = '*(flöde genererat — använd "Import to canvas")*';
const STREAMING_PLACEHOLDER = '*(genererar flöde…)*';

/**
 * Return true om textblocket ser ut som ett WorkflowState / compact-WorkflowState
 * (kräver ≥2 signatur-nycklar).
 */
export function looksLikeWorkflowJson(block: string): boolean {
  let hits = 0;
  for (const pat of WORKFLOW_KEY_PATTERNS) {
    if (pat.test(block)) hits++;
    if (hits >= 2) return true;
  }
  return false;
}

/**
 * Looser variant for IN-FLIGHT streaming detection. A single signature
 * key is enough — we want to mask raw JSON the moment it starts
 * appearing in the stream, before the full block has arrived. False
 * positives (1-key normal object) are acceptable here because the
 * placeholder is only shown transiently until the stream finishes;
 * the final pass uses `looksLikeWorkflowJson` (≥2 keys, strict).
 */
function looksLikeWorkflowJsonLoose(block: string): boolean {
  for (const pat of WORKFLOW_KEY_PATTERNS) {
    if (pat.test(block)) return true;
  }
  return false;
}

/**
 * Hitta balanced `{...}`-regions i texten och returnera deras [start, end]-
 * index (end är exklusiv). Respekterar strings och escape-sekvenser så att
 * `"}"`-tecken inne i strings inte räknas.
 */
function findBalancedObjectRegions(text: string): Array<{ start: number; end: number }> {
  const regions: Array<{ start: number; end: number }> = [];
  let i = 0;
  while (i < text.length) {
    if (text[i] !== '{') { i++; continue; }
    let depth = 0;
    let inString = false;
    let escape = false;
    let j = i;
    for (; j < text.length; j++) {
      const ch = text[j];
      if (escape) { escape = false; continue; }
      if (ch === '\\') { escape = true; continue; }
      if (ch === '"') { inString = !inString; continue; }
      if (inString) continue;
      if (ch === '{') depth++;
      else if (ch === '}') {
        depth--;
        if (depth === 0) { j++; break; }
      }
    }
    if (depth === 0 && j > i) {
      regions.push({ start: i, end: j });
      i = j;
    } else {
      // Unbalanced — ge upp, fortsätt scan:a från nästa tecken.
      i++;
    }
  }
  return regions;
}

/**
 * Returnera alla JSON-strängar som ser ut som WorkflowState — både fenced
 * (` ```json ``` `) och ofenced (top-level `{...}` i prosan). Caller kan parsa
 * dessa för att exponera "Import to canvas"-knappen.
 */
export function extractWorkflowJson(text: string): string[] {
  const out: string[] = [];

  // 1) Fenced ` ```json ... ``` `.
  const fencedRegex = /```(?:json)?\s*([\s\S]*?)```/g;
  let m: RegExpExecArray | null;
  while ((m = fencedRegex.exec(text)) !== null) {
    const content = m[1].trim();
    if (content.startsWith('{') && looksLikeWorkflowJson(content)) {
      out.push(content);
    }
  }

  // 2) Ofenced: ta bort fenced innehåll innan vi scannar — annars matchar vi
  // samma JSON två gånger.
  const withoutFences = text.replace(/```[\s\S]*?```/g, '');
  for (const { start, end } of findBalancedObjectRegions(withoutFences)) {
    const block = withoutFences.slice(start, end);
    if (looksLikeWorkflowJson(block)) out.push(block.trim());
  }

  return out;
}

/**
 * Find the index of the first UNCLOSED `{` in `text` (depth never
 * returns to 0). Returns -1 if every `{` has a matching `}`.
 * Respects strings + escape sequences so `"}"` inside a string
 * literal does not count.
 */
function findUnclosedObjectStart(text: string): number {
  // Walk all top-level `{` and check if each closes.
  let i = 0;
  while (i < text.length) {
    if (text[i] !== '{') { i++; continue; }
    let depth = 0;
    let inString = false;
    let escape = false;
    let j = i;
    let closed = false;
    for (; j < text.length; j++) {
      const ch = text[j];
      if (escape) { escape = false; continue; }
      if (ch === '\\') { escape = true; continue; }
      if (ch === '"') { inString = !inString; continue; }
      if (inString) continue;
      if (ch === '{') depth++;
      else if (ch === '}') {
        depth--;
        if (depth === 0) { closed = true; j++; break; }
      }
    }
    if (!closed) return i; // unclosed → this is the in-flight region
    i = j;
  }
  return -1;
}

/**
 * STREAMING-SAFE stripper. Runs `stripWorkflowJson` first to remove
 * any fully-closed workflow blocks, then ALSO masks:
 *   1. An unclosed ` ```json ... ` fence (no trailing ` ``` ` yet).
 *   2. An unclosed `{ ... ` object whose accumulated text already
 *      contains at least one workflow-signature key.
 *
 * The user never sees raw workflow JSON while the AI is still
 * streaming — only the "genererar flöde…" placeholder. The
 * "Import to canvas" button is gated by `extractWorkflowJson`
 * (closed blocks only), so it only appears when the JSON is
 * fully delivered and parseable.
 */
export function stripWorkflowJsonStreaming(
  text: string,
  finalPlaceholder: string = PLACEHOLDER,
  streamingPlaceholder: string = STREAMING_PLACEHOLDER,
): string {
  // Step 1: closed regions → final placeholder (idempotent).
  let out = stripWorkflowJson(text, finalPlaceholder);

  // Step 2: unclosed fence. After stripWorkflowJson, any remaining
  // ``` opens are unclosed. Match a single trailing fence and mask
  // its body if it carries a workflow signature.
  const fenceMatch = /```(?:json)?\s*([\s\S]*)$/.exec(out);
  if (fenceMatch && fenceMatch.index !== undefined) {
    const body = fenceMatch[1];
    if (body.includes('{') && looksLikeWorkflowJsonLoose(body)) {
      out = out.slice(0, fenceMatch.index).trimEnd();
      out = out.length > 0 ? `${out}\n\n${streamingPlaceholder}` : streamingPlaceholder;
    }
  }

  // Step 3: unclosed `{...` with workflow signature in the tail.
  const unclosedStart = findUnclosedObjectStart(out);
  if (unclosedStart >= 0) {
    const tail = out.slice(unclosedStart);
    if (looksLikeWorkflowJsonLoose(tail)) {
      const head = out.slice(0, unclosedStart).trimEnd();
      out = head.length > 0 ? `${head}\n\n${streamingPlaceholder}` : streamingPlaceholder;
    }
  }

  return out.replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Skrubba rå workflow-JSON från text innan rendering. Fenced block tas bort
 * helt; ofenced workflow-objekt ersätts med en placeholder så användaren ser
 * att något hände. Tomma rader kollapsas till max 2.
 */
export function stripWorkflowJson(text: string, placeholder: string = PLACEHOLDER): string {
  // Steg 1: fenced ` ```json ... ``` ` eller ` ``` { workflow } ``` ` → ta bort.
  let out = text.replace(/```(?:json)?\s*([\s\S]*?)```/g, (full, inner: string) => {
    const content = inner.trim();
    if (content.startsWith('{') && looksLikeWorkflowJson(content)) return placeholder;
    // Behåll fenced block som inte ser ut som workflow (kod-exempel etc.).
    return full;
  });

  // Steg 2: ofenced top-level objekt med workflow-signatur.
  const regions = findBalancedObjectRegions(out);
  if (regions.length > 0) {
    // Bygg ny sträng från bak till fram så indexen inte skiftar.
    for (let k = regions.length - 1; k >= 0; k--) {
      const { start, end } = regions[k];
      const block = out.slice(start, end);
      if (looksLikeWorkflowJson(block)) {
        out = out.slice(0, start) + placeholder + out.slice(end);
      }
    }
  }

  return out.replace(/\n{3,}/g, '\n\n').trim();
}
